import React, { useState, useContext, useEffect } from 'react';
import { UserContext } from '../../contexts/UserContext';
import NominationDetailsModal from './NominationDetailsModal';
import { Eye, ArrowUp, ArrowDown, Search, Download, Clock, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const UserMyNominations = ({ onNavigate, initialFilterStatus = 'all' }) => {
  const { user } = useContext(UserContext);
  const [nominations, setNominations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'status', direction: 'asc' });
  const [filterStatus, setFilterStatus] = useState(initialFilterStatus);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNomination, setSelectedNomination] = useState(null);
  const [showDetails, setShowDetails] = useState(false);  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  

  const fetchNominations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/nomination/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      setNominations(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching nominations:', error);
      setNominations([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNominations();
  }, []);

  // Reset to first page when items per page changes or when filtered results change
  useEffect(() => {
    const filteredData = getSortedAndFilteredNominations();
    const calculatedTotalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
    if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
      setCurrentPage(1);
    }
  }, [itemsPerPage, nominations.length, filterStatus, searchTerm, currentPage]);

  const handleSignNomination = (nomination) => {
    onNavigate('sign-nominations', null, { nomination });
  };

  const handleRowDoubleClick = (nomination) => {
    setSelectedNomination(nomination);
    if (nomination.status === 'pending') {
      handleSignNomination(nomination);
    } else {
      setShowDetails(true);
    }
  };

  const getShortScopeDisplayName = (scopeName) => {
    const scopeDisplayNames = {
      'supervised_planned_maintenance': 'Maintenance team member',
      'supervised_troubleshooting': 'Troubleshooting team member',
      'supervising_planned_as_nominated': 'Maintenance supervisor',
      'supervising_troubleshooting_as_nominated': 'Troubleshooting supervisor',
      'safe_workplace_preparation': 'Work station creator',
    };
    return scopeDisplayNames[scopeName] || scopeName;
  };

  const statusStyles = {
    pending: { text: 'Pending Review', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    signed: { text: 'Signed Pending', color: 'bg-orange-100 text-orange-800', icon: Clock },
    approved: { text: 'Active', color: 'bg-green-100 text-green-800', icon: CheckCircle },
    rejected: { text: 'Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
    expired: { text: 'Expired', color: 'bg-gray-100 text-gray-800', icon: AlertCircle },
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  const renderSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="h-3 w-3 ml-1" strokeWidth={1.5} />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1" strokeWidth={1.5} />
    );
  };

  const statusPriority = {
    pending: 1,
    signed: 2,
    approved: 3,
    rejected: 4,
    expired: 5,
  };

  const getSortedAndFilteredNominations = () => {
    const filteredNominations = (nominations || [])
      .filter((nomination) => 
        filterStatus === 'all' || nomination.status === filterStatus
      )
      .filter((nomination) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          !searchTerm || 
          (nomination.nomination_number && nomination.nomination_number.toLowerCase().includes(searchLower)) ||
          (nomination.company_name && nomination.company_name.toLowerCase().includes(searchLower)) ||
          (nomination.work_scope_data.name && nomination.work_scope_data.name.toLowerCase().includes(searchLower)) ||
          (nomination.company_to_data?.name && nomination.company_to_data.name.toLowerCase().includes(searchLower))
        );
      });
  
    return filteredNominations.sort((a, b) => {
      if (sortConfig.key === 'status') {
        return sortConfig.direction === 'asc'
          ? statusPriority[a.status] - statusPriority[b.status]
          : statusPriority[b.status] - statusPriority[a.status];
      }
  
      const valueA = a[sortConfig.key] ?? '';
      const valueB = b[sortConfig.key] ?? '';
      return sortConfig.direction === 'asc'
        ? String(valueA).localeCompare(String(valueB))
        : String(valueB).localeCompare(String(valueA));
    });
  };  

  const filteredNominations = getSortedAndFilteredNominations();
  const totalPages = Math.ceil(filteredNominations.length / itemsPerPage);
  const paginatedNominations = filteredNominations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getVisiblePages = () => {
    const maxVisiblePages = 5; // Bieżąca strona + 2 do przodu + 2 do tyłu
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="h-screen flex flex-col">
      {/* Header Section */}
      <div className="flex-shrink-0 p-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <h1 className="text-3xl font-bold text-slate-900">My Nominations</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 mx-4 mb-4 overflow-hidden">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-steel-100 h-full flex flex-col">
        {/* Search and Filter */}
        <div className="p-4 flex-shrink-0">
        <div className="flex flex-col md:flex-row justify-between mb-4 gap-4">
          <div className="relative flex-1 w-full">
            <Search className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              placeholder="Search by nomination, company..."
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-slate-300 rounded-lg px-4 py-2 w-60 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            aria-label="Filter by status"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="signed">Signed</option>
            <option value="approved">Active</option>
            <option value="rejected">Rejected</option>
            <option value="expired">Expired</option>
          </select>
        </div>
        </div>

        {/* Table container */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="flex-1 overflow-y-auto">
            <table className="min-w-full divide-y divide-steel-200">
              <thead className="bg-steel-100 border-b-2 border-brand-500 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                    <button
                      className="flex items-center gap-1 hover:text-slate-900"
                      onClick={() => handleSort('nomination_number')}
                    >
                      Nomination
                      {renderSortIndicator('nomination_number')}
                    </button>
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                    <button
                      className="flex items-center gap-1 hover:text-slate-900"
                      onClick={() => handleSort('date_start')}
                    >
                      Validity Period
                      {renderSortIndicator('date_start')}
                    </button>
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                    <button
                      className="flex items-center gap-1 hover:text-slate-900"
                      onClick={() => handleSort('company_name')}
                    >
                      Company
                      {renderSortIndicator('company_name')}
                    </button>
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                    Authorization Level
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                    <button
                      className="flex items-center gap-1 hover:text-slate-900"
                      onClick={() => handleSort('status')}
                    >
                      Status
                      {renderSortIndicator('status')}
                    </button>
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-bold text-slate-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-steel-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-500"></div>
                        Loading nominations...
                      </div>
                    </td>
                  </tr>
                ) : paginatedNominations.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                      No nominations available.
                    </td>
                  </tr>
                ) : (
                  paginatedNominations.map((nomination) => (
                    <tr
                      key={nomination.id}
                      className="cursor-pointer hover:bg-brand-50 transition-colors"
                      onDoubleClick={() => handleRowDoubleClick(nomination)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-900">
                        {nomination.nomination_number || 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                        {formatDate(nomination.date_start)} - {formatDate(nomination.date_end)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                        {nomination.company_name || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {Array.isArray(nomination.work_scope_data) && nomination.work_scope_data.length > 0 ? (
                          nomination.work_scope_data.length === 1 ? (
                            <div>{getShortScopeDisplayName(nomination.work_scope_data[0].name)}</div>
                          ) : (
                            <div>More than one level of authorization</div>
                          )
                        ) : (
                          <span className="text-slate-400">No authorization levels</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusStyles[nomination.status].color}`}>
                          {statusStyles[nomination.status].text}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                        <div className="flex justify-end gap-2">
                          {nomination.status === 'pending' ? (
                            <button
                              className="px-4 py-1.5 bg-brand-500 text-white text-xs font-bold rounded-lg hover:bg-brand-600 transition-colors shadow-sm"
                              onClick={() => { setSelectedNomination(nomination); handleSignNomination(nomination); }}
                              title="Sign"
                            >
                              Sign
                            </button>
                          ) : (
                            <button
                              className="p-1.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                              onClick={() => { setSelectedNomination(nomination); setShowDetails(true); }}
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            className="p-1.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                            onClick={() => {/* Handle download */}}
                            title="Download"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-steel-200 bg-slate-50">
            <div className="flex items-center gap-4">
              <div className="text-sm text-slate-600">
                Showing {(currentPage - 1) * itemsPerPage + 1}-
                {Math.min(currentPage * itemsPerPage, filteredNominations.length)} of {filteredNominations.length} items
              </div>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="text-sm px-3 py-1.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>
            <div className="flex space-x-2">
              {currentPage > 1 && (
                <button
                  className="px-3 py-1.5 text-sm border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors"
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mr-1"
                  >
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                  Previous
                </button>
              )}
              {getVisiblePages().map((page) => (
                <button
                  key={page}
                  className={`px-3 py-1.5 text-sm border ${
                    page === currentPage
                      ? 'border-brand-500 bg-brand-50 text-brand-700 font-bold'
                      : 'border-slate-300 text-slate-700 hover:bg-slate-100'
                  } rounded-lg flex items-center justify-center transition-colors`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
              {currentPage < totalPages && (
                <button
                  className="px-3 py-1.5 text-sm border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors"
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Next
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="ml-1"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>

      <NominationDetailsModal
        nomination={selectedNomination}
        showDetails={showDetails}
        setShowDetails={setShowDetails}
        statusStyles={statusStyles}
      />
    </div>
  );
};

export default UserMyNominations;